/**
 * The one place the two ends' patience is decided (D-093 §5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE CLIENT'S BUDGET IS DERIVED FROM THE WORKER'S WORST CASE, NOT GUESSED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Until C7.0's first live test these were two independent numbers with a
 *  comment between them. `src/config/worker.ts` said its 25 s was *"longer than
 *  the Worker's own 20 s timeout, deliberately… five seconds of headroom"* — and
 *  that reasoning was sized against **one** provider attempt while the same
 *  codebase retried once. The real worst case was 20 s × 2 ≈ 40 s, so the client
 *  could abort **fifteen seconds before the Worker was due to answer**.
 *
 *  **The consequence is the worst shape a bug can take here**: the meter charges
 *  before the provider, deliberately, so the user is billed for a call and then
 *  told *"the reviewer took too long to answer. Nothing was changed."* The money
 *  is spent and the app says nothing happened.
 *
 *  So the relationship is arithmetic now rather than prose. Raising the provider
 *  timeout or allowing a second retry moves the client's abort automatically, and
 *  **there is no way to change one without the other**. A test asserting an
 *  inequality between two hand-written constants would have been the weaker fix:
 *  it can only fail after someone has already written the wrong number.
 *
 *  This is the `SOLA_TOKEN_HEADER` lesson in its second application — **two ends
 *  that must agree get one constant, not two constants and a comment.**
 */

/**
 * How long the Worker waits for the provider, per attempt.
 *
 * Cut from 20 s at C7.0's first live test. A review is a foreground action with
 * a person watching, and the measured round trip is well under a second — twenty
 * seconds was never a latency budget, it was an upper bound nobody had tested,
 * and doubling it through the retry is what broke the client's arithmetic.
 */
export const PROVIDER_TIMEOUT_MS = 10_000

/**
 * Attempts, not retries. **Two means one retry**, which is the strict limit
 * D-093 §5 requires and the value `MAX_RETRIES` carried before.
 *
 * Expressed as attempts because that is the number the worst case multiplies by,
 * and an off-by-one here is exactly the mistake this file exists to prevent.
 */
export const PROVIDER_MAX_ATTEMPTS = 2

/**
 * Everything the Worker does that is not waiting for the provider: the meter,
 * the size and shape checks, response validation, the prose rules, and the
 * network hops at both ends.
 *
 * Generous on purpose. The cost of it being too large is a user waiting a few
 * extra seconds for a failure; the cost of it being too small is being charged
 * for an answer that was on its way.
 */
export const WORKER_OVERHEAD_MS = 5_000

/**
 * How long the client waits before aborting.
 *
 * **Derived, never chosen.** It must exceed the Worker's own worst case, because
 * a client that gives up first reports `requestTimedOut` for a call that was
 * charged and may have succeeded.
 */
export const CLIENT_TIMEOUT_MS = PROVIDER_TIMEOUT_MS * PROVIDER_MAX_ATTEMPTS + WORKER_OVERHEAD_MS
